/// <amd-module name="Comments/Thread/Components/ReplyOrAddComponent" />
import * as React from "react";

import { announce } from "VSS/Core/Util/Accessibility";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { IComment, ICommentArtifactInfo, ICommentsClient } from "Comments/Common/Contracts";
import * as Resources from "Comments/Thread/Resources";
import { AddComment } from "./AddComment";
import { IUICommentAdd } from "./CommentThreadInternalTypes";

export interface IReplyOrAddComponentProps {
    /**
     * Artifact info for loading more comments
     */
    artifactInfo: ICommentArtifactInfo;

    /**
     * Client for retrieving comments
     */
    commentsClient: ICommentsClient;

    /**
     * The add item as shown in the comment list
     */
    item: IUICommentAdd;

    /**
     * Whether or not the comments are readonly
     */
    readonly?: boolean;

    /**
     * Handler for adding new comments to the ui
     */
    add(comment: IComment, item: IUICommentAdd): void;

    /**
     * Handler for when an add operation is canceled
     */
    onCancelAdd(item: IUICommentAdd): void;

    /**
     * Render method for rendering a preview of the markdown text.
     * @param value string to render
     */
    onRenderPreview: (value: string) => JSX.Element;

    /**
     * Handler called when the comment editor has rendered
     */
    onEditorMounted: (item: IUICommentAdd) => void;

    /**
     * Handler for errors that come out of adding an item.
     */
    handleError(item: IUICommentAdd, error: any): void;

    /**
     * Optional on dirty flag change callback.
     * @param isDirty Value of dirty flag
     */
    onDirtyChange?: (isDirty: boolean) => void;

    /**
     * Optional upload image handler for the editor.
     * @param file File to upload
     */
    uploadImageHandler?: (file: File) => Promise<string>;
}

interface IReplyOrAddComponentState {
    isAddVisible: boolean;
    isAdding: boolean;
    text: string;
}

/**
 * Component for showing the add/reply button/link (internal use only)
 */
export class ReplyOrAddComponent extends VssComponent<IReplyOrAddComponentProps, IReplyOrAddComponentState> {
    constructor(props: IReplyOrAddComponentProps, context: IVssComponentContext) {
        super(props, context);

        this.state = { isAddVisible: false, isAdding: false, text: "" };
    }

    public render(): JSX.Element {
        const { readonly, item, onRenderPreview, onDirtyChange, uploadImageHandler } = this.props;
        const { isAddVisible, isAdding, text } = this.state;

        if (readonly) {
            return <></>;
        }

        const isChildThread: boolean = !!item.parentId;

        return (
            <>
                {(!isChildThread || isAddVisible) && !isAdding && (
                    <AddComment
                        onAdd={this._onAddComment}
                        onCancelEditing={this._onCancelAdd}
                        onStartEditing={this._onStartAdd}
                        onRenderPreview={onRenderPreview}
                        disabled={isAdding}
                        isEditing={isAddVisible}
                        onEditorMounted={this._onEditorMounted}
                        text={text}
                        onDirtyChange={onDirtyChange}
                        uploadImageHandler={uploadImageHandler}
                    />
                )}
                {isChildThread && !isAddVisible && !isAdding && (
                    <div className="reply-comments-link">
                        <Link onClick={this._onStartAdd}>{Resources.Reply}</Link>
                    </div>
                )}
                {isAdding && (
                    <span className="flex-row">
                        <Spinner className="comment-thread-status-spinner" size={SpinnerSize.small} />
                        <span>{Resources.Saving}</span>
                    </span>
                )}
            </>
        );
    }

    private _onEditorMounted = () => {
        const { onEditorMounted, item } = this.props;

        onEditorMounted(item);
    };

    private _onStartAdd = () => {
        this.setState({ isAddVisible: true, text: "" });
    };

    private _onCancelAdd = () => {
        const { onCancelAdd, item } = this.props;
        this.setState({ isAddVisible: false, text: "" });

        onCancelAdd(item);
    };

    private _onAddComment = async (text: string): Promise<void> => {
        const { artifactInfo, commentsClient, add, handleError, item } = this.props;

        try {
            this.setState({ isAdding: true, text: text });

            const comment: IComment = await commentsClient.addComment(artifactInfo, text, item.parentId);
            announce(Resources.CommentAddedMessage);
            this.setState({ isAdding: false, isAddVisible: false });
            add(comment, item);
        } catch (e) {
            this.setState({ isAdding: false });
            handleError(item, e);
        }
    };
}
